import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { StorageService } from 'src/app/services/storage.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';

interface DropboxFile {
  name: string;
  size: number;
  link: string;
  id: string;
  bytes: number;
  icon: string;
  thumbnailLink?: string;
  isDir: boolean;
}

declare var Dropbox: any;

@Component({
    selector: 'app-dropbox-modal',
    templateUrl: './dropbox-modal.component.html',
    styleUrls: ['./dropbox-modal.component.scss'],
    standalone: false
})
export class DropboxModalComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false;
  @Output() fileSelected = new EventEmitter<{
    file: File;
    base64: string;
    fileName: string;
  }>();
  @Output() modalClosed = new EventEmitter<void>();

  dropboxEnabled = false;
  isLoading: boolean = false;
  error: string = '';
  selectedFiles: DropboxFile[] = [];
  isDropboxLoaded: boolean = false;
  private readonly DROPBOX_APP_KEY = 'ktjba7f914f7f2r';

  constructor(
    private messageService: MessageService,
    private storageService: StorageService,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit() {
    // console.log('Initializing Dropbox modal');
    this.dropboxEnabled = this.featureFlagService.isOrgFeatureEnabled('dropboxEnabled');
    
    // ONLY load script if enabled
    if (this.dropboxEnabled) {
      this.loadDropboxScript();
    } else {
      console.warn('Dropbox disabled for this account');
    }
  }

  ngOnDestroy() {
    const existingScript = document.getElementById('dropboxjs');
    if (existingScript) {
      existingScript.remove();
    }
  }

  private loadDropboxScript(): void {
     if (!this.dropboxEnabled) {
      this.error = 'Dropbox integration is not available for this account.';
      return;
    }
    if (typeof Dropbox !== 'undefined') {
      this.isDropboxLoaded = true;
      // console.log('Dropbox already loaded');
      return;
    }

    const existingScript = document.getElementById('dropboxjs');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        this.isDropboxLoaded = true;
        // console.log('Dropbox loaded from existing script');
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.id = 'dropboxjs';
    script.setAttribute('data-app-key', this.DROPBOX_APP_KEY);

    script.onload = () => {
      this.isDropboxLoaded = true;
      // console.log('Dropbox API loaded successfully');
      // console.log(
      //   'Current domain:',
      //   window.location.hostname + ':' + window.location.port
      // );
    };

    script.onerror = (error) => {
      // console.error('Failed to load Dropbox script:', error);
      this.error =
        'Failed to load Dropbox API. Please check your app configuration.';
      this.messageService.add({
        severity: 'error',
        detail: 'Failed to load Dropbox integration. Please try again later.',
      });
    };

    document.head.appendChild(script);
  }

  openDropboxChooser(): void {
      if (!this.dropboxEnabled) {
      this.error = 'Dropbox integration is not available for this account.';
      return;
    }
    if (!this.isDropboxLoaded || typeof Dropbox === 'undefined') {
      this.error = 'Dropbox API not loaded yet. Please wait and try again.';
      this.messageService.add({
        severity: 'warn',
        detail: 'Dropbox is still loading. Please wait a moment and try again.',
      });
      return;
    }

    this.isLoading = true;
    this.error = '';

    const options = {
      success: (files: DropboxFile[]) => {
        // console.log('Dropbox files selected:', files);
        this.handleDropboxSuccess(files);
      },
      cancel: () => {
        // console.log('Dropbox chooser cancelled');
        this.isLoading = false;
        this.messageService.add({
          severity: 'info',
          detail: 'File selection cancelled',
        });
      },
      linkType: 'direct',
      multiselect: true,
      extensions: ['.pdf'],
      folderselect: false,
      sizeLimit: 50 * 1024 * 1024, // 50MB
    };

    try {
      Dropbox.choose(options);
    } catch (error) {
      // console.error('Error opening Dropbox chooser:', error);
      this.isLoading = false;
      this.error =
        'Failed to open Dropbox file chooser. Please check app configuration.';
      this.messageService.add({
        severity: 'error',
        detail:
          'Failed to open Dropbox file chooser. App may be misconfigured.',
      });
    }
  }

  private async handleDropboxSuccess(files: DropboxFile[]): Promise<void> {
    this.isLoading = true;
    this.error = '';

    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.messageService.add({
          severity: 'warn',
          detail: `${file.name} is not a PDF and will be skipped`,
        });
        continue;
      }

      if (file.bytes > 50 * 1024 * 1024) {
        this.messageService.add({
          severity: 'warn',
          detail: `${file.name} exceeds 50MB limit and will be skipped`,
        });
        continue;
      }

      try {
        const response = await fetch(file.link, {
          mode: 'cors',
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const newFile = new File([blob], file.name, {
          type: 'application/pdf',
        });

        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          this.fileSelected.emit({
            file: newFile,
            base64,
            fileName: file.name,
          });
        };
        reader.onerror = () => {
          this.messageService.add({
            severity: 'error',
            detail: `Failed to process ${file.name}`,
          });
        };
        reader.readAsDataURL(newFile);
      } catch (error) {
        // console.error(`Error processing ${file.name}:`, error);
        this.messageService.add({
          severity: 'error',
          detail: `Failed to process ${file.name}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        });
      }
    }

    this.isLoading = false;
    this.closeModal();
  }

  closeModal(): void {
    this.isVisible = false;
    this.isLoading = false;
    this.error = '';
    this.modalClosed.emit();
  }

  onModalHide(): void {
    this.closeModal();
  }
}
